/**
 * The 3-D view's inputs, decoded: GET /api/scene3d/{meta,terrain,frame,arrival}
 * (backend/app/routers/scene3d.py). Every array is the source's full-resolution
 * solver grid, row-major, north row first. The mesh drawn from them is decimated;
 * these arrays are not, and every value the page reports is read from them.
 */

export interface Lattice {
  n: number
  rows: number[]
  cols: number[]
  lon: number[]
  lat: number[]
}

export interface SceneMeta {
  source: string
  label: string
  solver: string
  site_id: string | null
  width: number
  height: number
  cell_m: number
  transform: number[]
  times_s: number[]
  has_vectors: boolean
  wet_threshold_m: number
  dem_source: string | null
  bounds_lonlat: [number, number, number, number]
  lattice: Lattice
  wet_counts: number[]
  peak_extent_frame: number | null
  breach: { lon: number; lat: number; source: string } | null
  settlements: { name: string; lon: number; lat: number; population: number | null }[]
  flood_bounds_lonlat?: [number, number, number, number]
  flood_centroid_lonlat?: [number, number]
  down_valley_bearing_deg?: number
}

export interface TerrainWire {
  width: number
  height: number
  min_m: number
  max_m: number
  p2_m: number
  p98_m: number
  void_cells: number
  void_rule: string
  dem_source: string | null
  elevation_f32: string
}

export interface Terrain {
  width: number
  height: number
  elevation: Float32Array
  min: number
  max: number
  p2: number
  p98: number
  voidCells: number
  voidRule: string
}

export interface FrameWire {
  t: number
  time_s: number
  depth_f32: string
  u_f32?: string
  v_f32?: string
}

export interface Frame {
  t: number
  timeS: number
  depth: Float32Array
  u: Float32Array | null
  v: Float32Array | null
}

export function b64f32(value: string): Float32Array {
  const bin = atob(value)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i)
  return new Float32Array(bytes.buffer)
}

export const decodeTerrain = (w: TerrainWire): Terrain => ({
  width: w.width,
  height: w.height,
  elevation: b64f32(w.elevation_f32),
  min: w.min_m,
  max: w.max_m,
  p2: w.p2_m,
  p98: w.p98_m,
  voidCells: w.void_cells,
  voidRule: w.void_rule,
})

export const decodeFrame = (w: FrameWire): Frame => ({
  t: w.t,
  timeS: w.time_s,
  depth: b64f32(w.depth_f32),
  u: w.u_f32 ? b64f32(w.u_f32) : null,
  v: w.v_f32 ? b64f32(w.v_f32) : null,
})

// ---------------------------------------------------------------- lon/lat of the grid

/** Lon/lat of the centre of (fractional) cell (row, col), bilinear in the server's lattice. */
export function cellLonLat(l: Lattice, row: number, col: number): [number, number] {
  const n = l.n
  const fr = (row / Math.max(1, l.rows[n - 1])) * (n - 1)
  const fc = (col / Math.max(1, l.cols[n - 1])) * (n - 1)
  const r0 = Math.max(0, Math.min(n - 2, Math.floor(fr)))
  const c0 = Math.max(0, Math.min(n - 2, Math.floor(fc)))
  const tr = fr - r0
  const tc = fc - c0
  const at = (arr: number[], r: number, c: number) => arr[r * n + c]
  const lerp = (arr: number[]) =>
    (at(arr, r0, c0) * (1 - tc) + at(arr, r0, c0 + 1) * tc) * (1 - tr) +
    (at(arr, r0 + 1, c0) * (1 - tc) + at(arr, r0 + 1, c0 + 1) * tc) * tr
  return [lerp(l.lon), lerp(l.lat)]
}

/** The (fractional) cell whose centre is at lon/lat: Newton iteration on cellLonLat. */
export function lonLatCell(l: Lattice, lon: number, lat: number): [number, number] {
  const n = l.n
  const H = l.rows[n - 1]
  const W = l.cols[n - 1]
  let row = H / 2
  let col = W / 2
  for (let it = 0; it < 12; it += 1) {
    const [x0, y0] = cellLonLat(l, row, col)
    const [xr, yr] = cellLonLat(l, row + 1, col)
    const [xc, yc] = cellLonLat(l, row, col + 1)
    const a = xc - x0
    const b = xr - x0
    const c = yc - y0
    const d = yr - y0
    const det = a * d - b * c
    if (!det) break
    const ex = lon - x0
    const ey = lat - y0
    const dc = (d * ex - b * ey) / det
    const dr = (-c * ex + a * ey) / det
    col += dc
    row += dr
    if (Math.abs(dc) < 1e-4 && Math.abs(dr) < 1e-4) break
  }
  return [row, col]
}

export function clock(seconds: number): string {
  const sign = seconds < 0 ? '−' : '+'
  const total = Math.round(Math.abs(seconds) / 60)
  const h = Math.floor(total / 60)
  const m = total % 60
  return `T${sign}${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}
